import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Alert,
    Chip,
    CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';

interface QuestionPaper {
    paper_id: number;
    title: string;
    description: string;
    theme_id: number;
    theme_title: string;
    activation_date: string;
    expiry_date: string;
    questions_count: number;
    attempted: boolean;
    is_active: boolean;
    created_by: number;
    created_at: string;
    updated_at: string;
    certificate_path: string | null;
    grade_brackets: string;
}

const PublicQuizzes: React.FC = () => {
    const { t } = useTranslation();
    const [papers, setPapers] = useState<QuestionPaper[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        const fetchPapers = async () => {
            try {
                const response = await api.get('/quiz/public/papers');
                // Sort papers by activation date in descending order
                const sortedPapers = response.data.sort((a: QuestionPaper, b: QuestionPaper) => 
                    new Date(b.activation_date).getTime() - new Date(a.activation_date).getTime()
                );
                setPapers(sortedPapers);
            } catch (error: any) {
                console.error('Failed to fetch papers:', error);
                setError(error.response?.data?.error || 'Failed to load available quizzes');
            } finally {
                setLoading(false);
            }
        };

        fetchPapers();
    }, []);

    const handleStartQuiz = async (paperId: number) => {
        try {
            const response = await api.post('/quiz/attempts', { 
                paper_id: paperId,
                guest_attempt: !isAuthenticated
            });
            
            if (response.data && response.data.attempt_id) {
                navigate(`/quiz/${response.data.attempt_id}`);
            } else {
                setError('Failed to start quiz: Invalid response from server');
            }
        } catch (error: any) {
            console.error('Quiz start error:', error);
            
            if (error.response?.status === 403) {
                if (!isAuthenticated) {
                    navigate('/register', { 
                        state: { 
                            message: 'Register to unlock more quizzes!',
                            returnTo: '/'
                        } 
                    });
                } else if (!user?.is_subscribed) {
                    navigate('/subscription', { 
                        state: { 
                            message: 'Subscribe to unlock unlimited quizzes!',
                            returnTo: '/'
                        } 
                    });
                } else {
                    setError('You do not have permission to attempt this quiz');
                }
            } else {
                setError(error.response?.data?.error || 'Failed to start quiz. Please try again.');
            }
        }
    };

    const getQuizStatus = (paper: QuestionPaper) => {
        const now = new Date();
        const activationDate = new Date(paper.activation_date);
        const expiryDate = new Date(paper.expiry_date);

        if (!paper.is_active) {
            return { 
                label: 'Inactive', 
                color: 'error' as const,
                bgcolor: '#ffebee',
                textcolor: '#d32f2f'
            };
        } else if (paper.attempted) {
            return { 
                label: 'Attempted', 
                color: 'default' as const,
                bgcolor: '#f5f5f5',
                textcolor: '#666666'
            };
        } else if (now < activationDate) {
            return { 
                label: t('quiz.comingSoon'), 
                color: 'warning' as const,
                bgcolor: '#fff3e0',
                textcolor: '#f57c00'
            };
        } else if (now > expiryDate) {
            return { 
                label: 'Past Paper', 
                color: 'secondary' as const,
                bgcolor: '#e8eaf6',
                textcolor: '#3f51b5'
            };
        } else {
            return { 
                label: 'Active', 
                color: 'success' as const,
                bgcolor: '#e8f5e9',
                textcolor: '#2e7d32'
            };
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Available Quizzes
                </Typography>
                {!isAuthenticated ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                display: 'inline'
                            }}
                        >
                            Try a past paper for free!
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/register')}
                            sx={{ 
                                textTransform: 'none',
                                backgroundColor: '#2e7d32',
                                '&:hover': {
                                    backgroundColor: '#1b5e20'
                                },
                                ml: 1,
                                px: 2
                            }}
                        >
                            Register to unlock more features
                        </Button>
                    </Box>
                ) : !(user?.is_subscribed ?? false) ? (
                    <Typography variant="body1" color="text.secondary">
                        You can attempt one current or past paper. Subscribe to unlock unlimited access!
                    </Typography>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        Browse and attempt available quizzes.
                    </Typography>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {papers.map((paper) => {
                    const status = getQuizStatus(paper);
                    const isDisabled = !paper.is_active || 
                        paper.attempted || 
                        new Date() < new Date(paper.activation_date) ||
                        (!isAuthenticated && status.label !== 'Past Paper') ||
                        (isAuthenticated && !(user?.is_subscribed ?? false) && status.label !== 'Past Paper' && status.label !== 'Active');

                    return (
                        <Grid item xs={12} md={6} lg={4} key={paper.paper_id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="h6" component="div">
                                            {paper.title}
                                        </Typography>
                                        <Chip 
                                            label={status.label} 
                                            size="small"
                                            sx={{
                                                borderRadius: '4px',
                                                backgroundColor: status.bgcolor,
                                                color: status.textcolor,
                                                fontWeight: 500,
                                                '& .MuiChip-label': {
                                                    px: 1.5
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        {paper.description}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Theme: {paper.theme_title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Questions: {paper.questions_count}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Available: {format(new Date(paper.activation_date), 'dd MMM yyyy')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Expires: {format(new Date(paper.expiry_date), 'dd MMM yyyy')}
                                        </Typography>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        onClick={() => handleStartQuiz(paper.paper_id)}
                                        disabled={isDisabled}
                                    >
                                        {paper.attempted ? 'Already Attempted' : 'Start Quiz'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    );
};

export default PublicQuizzes;
